import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';

import { api, DeleteSiteRequest, DeleteUserRequest, CreateSiteRequest, UpdateSiteSettingsRequest, getDateRange } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';

// Query keys for React Query
export const queryKeys = {
  stats: (siteId: string, from: string, to: string) => 
    ['stats', siteId, from, to] as const,
  planStatus: (siteId: string) => ['planStatus', siteId] as const,
  userSites: () => ['userSites'] as const,
  health: () => ['health'] as const,
};

// Hook for getting stats
export function useStats(siteId: string, period: 'week' | 'month' | 'year') {
  const { user } = useAuth();
  
  // Memoize the date range to prevent unstable query keys
  const { from, to } = useMemo(() => getDateRange(period), [period]);

  return useQuery({
    queryKey: queryKeys.stats(siteId, from, to),
    queryFn: () => api.getStats({ siteId, from, to }),
    enabled: !!user && !!siteId && siteId.trim() !== '', // Prevent empty siteId calls
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 2,
    refetchOnWindowFocus: false,
  });
}

// Hook for getting plan status
export function usePlanStatus(siteId: string) {
  const { user } = useAuth();

  return useQuery({
    queryKey: queryKeys.planStatus(siteId),
    queryFn: () => api.getPlanStatus(siteId),
    enabled: !!user && !!siteId && siteId.trim() !== '', // Prevent empty siteId calls
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 2,
    refetchOnWindowFocus: false,
  });
}

// Hook for getting user's sites
export function useUserSites() {
  const { user } = useAuth();

  return useQuery({
    queryKey: queryKeys.userSites(),
    queryFn: () => api.getUserSites(),
    enabled: !!user,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 2,
    refetchOnWindowFocus: false,
  });
}

// Hook for creating a new site (mutation)
export function useCreateSite() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: CreateSiteRequest) => api.createSite(params),
    onSuccess: () => {
      // Invalidate and refetch user sites after successful creation
      queryClient.invalidateQueries({ queryKey: queryKeys.userSites() });
    },
  });
}

// Hook for creating default site for new user (mutation)
export function useCreateDefaultSite() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => api.createDefaultSite(),
    onSuccess: () => {
      // Invalidate and refetch user sites after successful creation
      queryClient.invalidateQueries({ queryKey: queryKeys.userSites() });
    },
  });
}

// Hook for updating site settings (mutation)
export function useUpdateSiteSettings() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: UpdateSiteSettingsRequest) => api.updateSiteSettings(params),
    onSuccess: () => {
      // Invalidate and refetch user sites after successful update
      queryClient.invalidateQueries({ queryKey: queryKeys.userSites() });
    },
  });
}

// Hook for deleting a site (mutation)
export function useDeleteSite() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: DeleteSiteRequest) => api.deleteSite(params),
    onSuccess: () => {
      // Invalidate and refetch user sites after successful deletion
      queryClient.invalidateQueries({ queryKey: queryKeys.userSites() });
    },
  });
}

// Hook for deleting user account (mutation)
export function useDeleteUser() {
  return useMutation({
    mutationFn: (params: DeleteUserRequest) => api.deleteUser(params),
  });
}

// Hook for health check
export function useHealthCheck() {
  return useQuery({
    queryKey: queryKeys.health(),
    queryFn: () => api.healthCheck(),
    staleTime: 30 * 1000, // 30 seconds
    retry: 1,
    refetchOnWindowFocus: false,
  });
}

// Hook for getting aggregated stats across all sites
export function useAggregatedStats() {
  const { user } = useAuth();
  const sitesQuery = useUserSites();

  return useQuery({
    queryKey: ['aggregatedStats'],
    queryFn: async () => {
      if (!sitesQuery.data || sitesQuery.data.length === 0) {
        return {
          totalVotes: 0,
          upVotes: 0,
          downVotes: 0,
          conversionRate: 0,
          sites: 0,
        };
      }

      // Get date range for the last month (consistent with individual site stats)
      const { from, to } = getDateRange('month');

      // Fetch real stats for each site in parallel
      const statsPromises = sitesQuery.data.map(site => 
        api.getStats({ siteId: site.id, from, to }).catch(error => {
          console.warn(`Failed to fetch stats for site ${site.id}:`, error);
          // Return zero stats if a site fails to prevent the entire aggregation from failing
          return {
            totalVotes: 0,
            upVotes: 0,
            downVotes: 0,
            conversionRate: 0,
            timeSeries: [],
            countries: {},
            regions: {},
            cities: {},
            pages: {},
            comments: [],
          };
        })
      );

      // Wait for all stats to be fetched
      const allStats = await Promise.all(statsPromises);

      // Aggregate real stats from all sites
      const aggregated = allStats.reduce(
        (acc, stats) => ({
          totalVotes: acc.totalVotes + stats.totalVotes,
          upVotes: acc.upVotes + stats.upVotes,
          downVotes: acc.downVotes + stats.downVotes,
          sites: acc.sites + 1,
        }),
        { totalVotes: 0, upVotes: 0, downVotes: 0, sites: 0 }
      );

      return {
        ...aggregated,
        conversionRate: aggregated.totalVotes > 0 ? aggregated.upVotes / aggregated.totalVotes : 0,
      };
    },
    enabled: !!user && !sitesQuery.isLoading && !!sitesQuery.data,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 2,
  });
}

// Hook for getting combined dashboard data
export function useDashboardData(siteId?: string) {
  // Don't make redundant calls - assume sites data is already available
  // Get stats for the active site only if siteId is valid
  const statsQuery = useStats(siteId || '', 'month');
  
  // Get plan status for the active site only if siteId is valid
  const planQuery = usePlanStatus(siteId || '');

  return {
    stats: statsQuery,
    plan: planQuery,
    activeSiteId: siteId,
    isLoading: statsQuery.isLoading || planQuery.isLoading,
    error: statsQuery.error || planQuery.error,
  };
} 